import { useCallback, useEffect, useMemo, useState } from 'react'
import { appyAdapter } from './appy-adapter'
import { defaultSnapshot } from './demo-data'
import type { AppKind, AppVersion, AppyCreation, AppySnapshot, DailySchedule, GenerationRequest, GenerationResult, Intelligence, ProfileUpdate } from './model'

const clone = (): AppySnapshot => JSON.parse(JSON.stringify(defaultSnapshot)) as AppySnapshot
const message = (error: unknown, fallback: string) => error instanceof Error && error.message ? error.message : fallback

export interface NewBuild {
  id: string
  prompt: string
  kind: AppKind
  title?: string
}

const mergePreserveBuilding = (previous: AppySnapshot, next: AppySnapshot): AppySnapshot => {
  const previousById = new Map(previous.apps.map((app) => [app.id, app]))
  const merged = next.apps.map((app) => {
    const prior = previousById.get(app.id)
    if (!prior) return app
    // Keep live client build log while the server still reports building.
    if (prior.status === 'building' && app.status === 'building') {
      return {
        ...app,
        buildLog: prior.buildLog?.length ? prior.buildLog : app.buildLog,
        description: prior.description || app.description,
      }
    }
    // Server finished/failed — adopt server state but keep local progress history.
    if (prior.status === 'building' && prior.buildLog?.length) {
      return { ...app, buildLog: [...prior.buildLog, ...(app.buildLog ?? [])] }
    }
    return app
  })
  // Optimistic local builds not yet present on the server snapshot.
  for (const prior of previous.apps) {
    if (prior.status === 'building' && !merged.some((app) => app.id === prior.id)) {
      merged.unshift(prior)
    }
  }
  return { ...next, apps: merged }
}

export function useAppyStore() {
  const [snapshot, setSnapshot] = useState<AppySnapshot>(clone)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    let finishTimer: number | undefined
    setLoading(true)
    setLoadError(null)
    const appId = new URLSearchParams(window.location.search).get('app')
    const nav = typeof performance !== 'undefined'
      ? performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
      : undefined
    const cancelActiveBuilds = nav?.type === 'reload'
      || (typeof performance !== 'undefined' && (performance as { navigation?: { type?: number } }).navigation?.type === 1)
    appyAdapter.loadSnapshot({ appId, cancelActiveBuilds }).then((next) => {
      if (!active) return
      setSnapshot(next ?? clone())
      finishTimer = window.setTimeout(() => active && setLoading(false), 500)
    }).catch((error: unknown) => {
      if (!active) return
      setLoadError(message(error, 'Appy could not connect to its servers.'))
      setLoading(false)
    })
    return () => { active = false; if (finishTimer !== undefined) window.clearTimeout(finishTimer) }
  }, [loadAttempt])

  useEffect(() => {
    if (!loading && !loadError) void appyAdapter.saveSnapshot(snapshot).catch((error: unknown) => {
      console.warn('Appy could not save the latest account state.', error)
    })
  }, [snapshot, loading, loadError])

  const retryLoad = useCallback(() => setLoadAttempt((attempt) => attempt + 1), [])
  /** Soft refresh: reloads feed/account data without a browser reload, so builds keep running. */
  const softRefresh = useCallback(async () => {
    if (refreshing) return false
    setRefreshing(true)
    setActionError(null)
    try {
      const appId = new URLSearchParams(window.location.search).get('app')
      const next = await appyAdapter.loadSnapshot({ appId, cancelActiveBuilds: false })
      if (!next) throw new Error('Appy could not refresh right now.')
      setSnapshot((current) => mergePreserveBuilding(current, next))
      setLoadError(null)
      return true
    } catch (error: unknown) {
      setActionError(message(error, 'Could not refresh Appy.'))
      return false
    } finally {
      setRefreshing(false)
    }
  }, [refreshing])
  const clearActionError = useCallback(() => setActionError(null), [])
  const reportActionError = useCallback((error: unknown, fallback: string) => setActionError(message(error, fallback)), [])

  const update = useCallback((recipe: (current: AppySnapshot) => AppySnapshot) => setSnapshot((current) => recipe(current)), [])

  const patchApp = useCallback((id: string, recipe: (app: AppyCreation) => AppyCreation) => {
    update((current) => ({ ...current, apps: current.apps.map((app) => app.id === id ? recipe(app) : app) }))
  }, [update])

  const toggleLike = useCallback((id: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    if (!app) return
    const likedByMe = !app.likedByMe
    const likes = Math.max(0, app.likes + (app.likedByMe ? -1 : 1))
    patchApp(id, (item) => ({ ...item, likedByMe: !item.likedByMe, likes: Math.max(0, item.likes + (item.likedByMe ? -1 : 1)) }))
    void appyAdapter.toggleLike?.(app).catch((error: unknown) => {
      patchApp(id, (item) => item.likedByMe === likedByMe && item.likes === likes ? { ...item, likedByMe: app.likedByMe, likes: app.likes } : item)
      reportActionError(error, 'Could not update this heart.')
    })
  }, [patchApp, reportActionError, snapshot.apps])
  const toggleSave = useCallback((id: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    if (!app) return
    const savedByMe = !app.savedByMe
    const saves = Math.max(0, app.saves + (app.savedByMe ? -1 : 1))
    patchApp(id, (item) => ({ ...item, savedByMe: !item.savedByMe, saves: Math.max(0, item.saves + (item.savedByMe ? -1 : 1)) }))
    void appyAdapter.toggleSave?.(app).catch((error: unknown) => {
      patchApp(id, (item) => item.savedByMe === savedByMe && item.saves === saves ? { ...item, savedByMe: app.savedByMe, saves: app.saves } : item)
      reportActionError(error, 'Could not update your Library.')
    })
  }, [patchApp, reportActionError, snapshot.apps])
  const toggleRepost = useCallback((id: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    if (!app) return
    const repostedByMe = !app.repostedByMe
    const reposts = Math.max(0, app.reposts + (app.repostedByMe ? -1 : 1))
    patchApp(id, (item) => ({ ...item, repostedByMe: !item.repostedByMe, reposts: Math.max(0, item.reposts + (item.repostedByMe ? -1 : 1)) }))
    void appyAdapter.toggleRepost?.(app).catch((error: unknown) => {
      patchApp(id, (item) => item.repostedByMe === repostedByMe && item.reposts === reposts ? { ...item, repostedByMe: app.repostedByMe, reposts: app.reposts } : item)
      reportActionError(error, 'Could not update this repost.')
    })
  }, [patchApp, reportActionError, snapshot.apps])
  const recordPlay = useCallback((id: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    patchApp(id, (item) => ({ ...item, plays: item.plays + 1, updatedAt: new Date().toISOString() }))
    if (app) void appyAdapter.recordPlay?.(app)
  }, [patchApp, snapshot.apps])

  const toggleFollow = useCallback((userId: string) => {
    const user = snapshot.users.find((item) => item.id === userId)
    if (!user) return
    const followedByMe = !user.followedByMe
    const followers = Math.max(0, user.followers + (user.followedByMe ? -1 : 1))
    update((current) => ({ ...current, users: current.users.map((user) => user.id === userId ? { ...user, followedByMe: !user.followedByMe, followers: Math.max(0, user.followers + (user.followedByMe ? -1 : 1)) } : user) }))
    void appyAdapter.toggleFollow?.(userId).catch((error: unknown) => {
      update((current) => ({ ...current, users: current.users.map((item) => item.id === userId && item.followedByMe === followedByMe && item.followers === followers ? { ...item, followedByMe: user.followedByMe, followers: user.followers } : item) }))
      reportActionError(error, 'Could not update this follow.')
    })
  }, [reportActionError, snapshot.users, update])

  const addComment = useCallback((appId: string, body: string, parentId?: string | null) => {
    if (!body.trim()) return
    const createdAt = new Date().toISOString()
    const tempId = crypto.randomUUID()
    const trimmed = body.trim()
    // Only nest under parents that look like real server rows (not another optimistic temp id
    // that has not been confirmed yet — those fail with "parent must belong to the same post").
    const safeParent = parentId && snapshot.comments.some((item) => item.id === parentId && !item.id.startsWith('local-'))
      ? parentId
      : parentId && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parentId)
        ? parentId
        : null
    update((current) => ({
      ...current,
      comments: [{
        id: tempId, appId, userId: current.currentUserId, body: trimmed, createdAt, likes: 0,
        parentId: safeParent, replyCount: 0, reposts: 0, repostedByMe: false,
      }, ...current.comments.map((comment) => comment.id === safeParent
        ? { ...comment, replyCount: (comment.replyCount ?? 0) + 1 }
        : comment)],
      apps: current.apps.map((app) => app.id === appId ? { ...app, comments: app.comments + 1 } : app),
    }))
    const app = snapshot.apps.find((item) => item.id === appId)
    if (app) {
      void appyAdapter.addComment?.(app, trimmed, safeParent).then((serverId) => {
        if (typeof serverId === 'string' && serverId) {
          update((current) => ({
            ...current,
            comments: current.comments.map((comment) => comment.id === tempId ? { ...comment, id: serverId } : comment),
          }))
        }
      }).catch((error: unknown) => {
        update((current) => ({
          ...current,
          comments: current.comments
            .filter((comment) => comment.id !== tempId)
            .map((comment) => comment.id === safeParent
              ? { ...comment, replyCount: Math.max(0, (comment.replyCount ?? 1) - 1) }
              : comment),
          apps: current.apps.map((item) => item.id === appId ? { ...item, comments: Math.max(0, item.comments - 1) } : item),
        }))
        reportActionError(error, 'Could not post your comment.')
      })
    }
  }, [reportActionError, snapshot.apps, snapshot.comments, update])

  const recordImpression = useCallback((postId: string, dwellMs: number, surface = 'for_you', opened = false) => {
    void appyAdapter.recordImpression?.(postId, dwellMs, surface, opened)
  }, [])

  const toggleCommentLike = useCallback((commentId: string) => {
    const comment = snapshot.comments.find((item) => item.id === commentId)
    if (!comment) return
    const likedByMe = !comment.likedByMe
    const likes = Math.max(0, comment.likes + (comment.likedByMe ? -1 : 1))
    update((current) => ({ ...current, comments: current.comments.map((item) => item.id === commentId ? { ...item, likedByMe: !item.likedByMe, likes: Math.max(0, item.likes + (item.likedByMe ? -1 : 1)) } : item) }))
    void appyAdapter.toggleCommentLike?.(commentId, likedByMe).catch((error: unknown) => {
      update((current) => ({ ...current, comments: current.comments.map((item) => item.id === commentId && item.likedByMe === likedByMe && item.likes === likes ? { ...item, likedByMe: comment.likedByMe, likes: comment.likes } : item) }))
      reportActionError(error, 'Could not update this comment heart.')
    })
  }, [reportActionError, snapshot.comments, update])

  const toggleCommentRepost = useCallback((commentId: string) => {
    const comment = snapshot.comments.find((item) => item.id === commentId)
    if (!comment) return
    const nextReposted = !comment.repostedByMe
    const nextReposts = Math.max(0, (comment.reposts ?? 0) + (comment.repostedByMe ? -1 : 1))
    update((current) => ({
      ...current,
      comments: current.comments.map((item) => item.id === commentId
        ? { ...item, repostedByMe: nextReposted, reposts: nextReposts }
        : item),
    }))
    void appyAdapter.toggleCommentRepost?.(commentId, Boolean(comment.repostedByMe)).catch((error: unknown) => {
      update((current) => ({
        ...current,
        comments: current.comments.map((item) => item.id === commentId
          ? { ...item, repostedByMe: comment.repostedByMe, reposts: comment.reposts ?? 0 }
          : item),
      }))
      reportActionError(error, 'Could not repost this comment.')
    })
  }, [reportActionError, snapshot.comments, update])

  const appendBuildLog = useCallback((id: string, message: string, status?: string, file?: string) => {
    const entry = { message, status, file, at: new Date().toISOString() }
    patchApp(id, (app) => {
      const log = app.buildLog ?? []
      const last = log[log.length - 1]
      // Avoid spammy identical lines when the poller repeats the same stage.
      if (last && last.message === message && last.file === file) {
        return { ...app, description: message }
      }
      return { ...app, buildLog: [...log, entry], description: message }
    })
  }, [patchApp])

  const beginBuild = useCallback((build: NewBuild) => {
    const now = new Date().toISOString()
    update((current) => ({
      ...current,
      apps: [{ id: build.id, authorId: current.currentUserId, title: build.title?.trim() || 'Untitled App', description: 'Starting build…', prompt: build.prompt, kind: build.kind, status: 'building', createdAt: now, updatedAt: now, srcDoc: '', coverClass: 'cover-building', tags: ['Building'], likes: 0, comments: 0, reposts: 0, saves: 0, plays: 0, version: 0, versions: [], postType: 'app', buildLog: [{ message: 'Queued — preparing your prompt for the model…', at: now, status: 'queued' }] }, ...current.apps],
    }))
  }, [update])

  const finishBuild = useCallback((id: string, result: GenerationResult) => {
    const now = new Date().toISOString()
    update((current) => ({
      ...current,
      billing: current.billing.unlimited ? current.billing : { ...current.billing, creditBalance: Math.max(0, current.billing.creditBalance - result.estimatedCost), usageThisMonth: current.billing.usageThisMonth + result.estimatedCost },
      apps: current.apps.map((app) => app.id === id ? { ...app, ...result, status: 'ready', updatedAt: now, version: 1, versions: [{ number: 1, note: 'Initial build', createdAt: now, srcDoc: result.srcDoc }], buildLog: [...(app.buildLog ?? []), { message: 'Ready for review. Write your post caption, then publish.', at: now, status: 'ready' }] } : app),
      notifications: [{ id: crypto.randomUUID(), type: 'build', title: `${result.title} is ready`, body: 'Review, write your post, and publish.', createdAt: now, read: false, appId: id }, ...current.notifications],
    }))
  }, [update])

  const build = useCallback(async (request: GenerationRequest) => {
    const id = crypto.randomUUID()
    beginBuild({ id, prompt: request.prompt, kind: request.kind, title: request.title })
    void appyAdapter.generate({
      ...request,
      projectId: id,
      onProgress: (entry) => appendBuildLog(id, entry.message, entry.status, entry.file),
    }).then((result) => finishBuild(id, result)).catch((error: unknown) => {
      const now = new Date().toISOString()
      update((current) => ({ ...current, apps: current.apps.map((app) => app.id === id ? { ...app, status: 'failed', description: error instanceof Error ? error.message : 'The build failed. You can retry it.', updatedAt: now, buildLog: [...(app.buildLog ?? []), { message: error instanceof Error ? error.message : 'The build failed.', at: now, status: 'failed' }] } : app), notifications: [{ id: crypto.randomUUID(), type: 'build', title: 'Build needs attention', body: error instanceof Error ? error.message : 'The build failed.', createdAt: now, read: false, appId: id }, ...current.notifications] }))
    })
    return id
  }, [appendBuildLog, beginBuild, finishBuild, update])

  const publish = useCallback((id: string, body?: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    const now = new Date().toISOString()
    const caption = (body ?? app?.postBody ?? app?.description ?? '').trim()
    patchApp(id, (item) => ({ ...item, status: 'published', publishedAt: now, updatedAt: now, postBody: caption, postType: 'app' }))
    if (app) {
      const publishing = appyAdapter.publish?.(app, caption)
      if (publishing) void publishing.then((postId) => {
        if (postId) patchApp(id, (item) => ({ ...item, postId, postBody: caption }))
      }).catch((error: unknown) => {
        patchApp(id, (item) => ({ ...item, status: app.status, publishedAt: app.publishedAt, description: error instanceof Error ? error.message : item.description }))
      })
    }
  }, [patchApp, snapshot.apps])

  const cancelBuild = useCallback(async (id?: string) => {
    if (!appyAdapter.cancelBuild) throw new Error('Stop is not available in this build.')
    const count = await appyAdapter.cancelBuild(id)
    const now = new Date().toISOString()
    update((current) => ({
      ...current,
      apps: current.apps.map((app) => {
        if (app.status !== 'building') return app
        if (id && app.id !== id) return app
        return {
          ...app,
          status: app.version > 0 ? (app.publishedAt ? 'published' : 'ready') : 'failed',
          description: 'Build stopped.',
          updatedAt: now,
          buildLog: [...(app.buildLog ?? []), { message: 'Build stopped.', at: now, status: 'failed', file: 'stop' }],
        }
      }),
    }))
    return count
  }, [update])

  const revise = useCallback(async (id: string, instruction: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    if (!app || !instruction.trim()) throw new Error('Pick an app and describe one change first.')
    if (app.status === 'building') throw new Error('This app is already building. Wait for it to finish, then try again.')
    const priorStatus = app.status
    const changeNote = instruction.trim()
    patchApp(id, (item) => ({
      ...item,
      status: 'building',
      updatedAt: new Date().toISOString(),
      buildLog: [...(item.buildLog ?? []), { message: `Updating: ${changeNote}`, at: new Date().toISOString(), status: 'queued' }],
    }))
    try {
      const result = await appyAdapter.revise(app, changeNote, snapshot.settings.intelligence, (entry) => appendBuildLog(id, entry.message, entry.status, entry.file))
      if (!result?.srcDoc?.trim()) throw new Error('The update finished without new app code. Try the change again.')
      const now = new Date().toISOString()
      const nextStatus = priorStatus === 'published' ? 'published' : 'ready'
      update((current) => ({
        ...current,
        billing: current.billing.unlimited ? current.billing : { ...current.billing, creditBalance: Math.max(0, current.billing.creditBalance - result.estimatedCost), usageThisMonth: current.billing.usageThisMonth + result.estimatedCost },
        apps: current.apps.map((item) => {
          if (item.id !== id) return item
          const nextVersion = Math.max(item.version, 0) + 1
          return {
            ...item,
            ...result,
            status: nextStatus,
            version: nextVersion,
            updatedAt: now,
            // Keep published posts playable immediately with the new source.
            publishedAt: nextStatus === 'published' ? (item.publishedAt ?? now) : item.publishedAt,
            versions: [...item.versions, { number: nextVersion, note: changeNote, createdAt: now, srcDoc: result.srcDoc, title: result.title, description: result.description }],
            buildLog: [...(item.buildLog ?? []), { message: `Update applied: ${changeNote}`, at: now, status: 'ready' }],
          }
        }),
        notifications: [{ id: crypto.randomUUID(), type: 'build', title: `${result.title || app.title} was updated`, body: changeNote, createdAt: now, read: false, appId: id }, ...current.notifications],
      }))
    } catch (error) {
      const failMessage = message(error, 'Could not update this app.')
      const now = new Date().toISOString()
      patchApp(id, (item) => ({
        ...item,
        status: priorStatus,
        description: failMessage,
        updatedAt: now,
        buildLog: [...(item.buildLog ?? []), { message: failMessage, at: now, status: 'failed' }],
      }))
      throw error instanceof Error ? error : new Error(failMessage)
    }
  }, [appendBuildLog, snapshot.apps, snapshot.settings.intelligence, patchApp, update])

  const deletePost = useCallback(async (id: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    if (!app?.postId) throw new Error('This item is not a published post.')
    await appyAdapter.deletePost?.(app.postId)
    if (app.postType === 'text' || app.postType === 'short') update((current) => ({ ...current, apps: current.apps.filter((item) => item.id !== id) }))
    else patchApp(id, (item) => ({ ...item, status: 'ready', postId: undefined, publishedAt: undefined, postBody: undefined }))
  }, [patchApp, snapshot.apps, update])

  const updatePost = useCallback(async (id: string, body: string) => {
    const app = snapshot.apps.find((item) => item.id === id)
    // Text/short posts use the post UUID as id; apps use project id + separate postId.
    const postId = app?.postId ?? (app?.postType === 'text' || app?.postType === 'short' ? app.id : null)
    if (!app || !postId) throw new Error('This item is not a published post.')
    await appyAdapter.updatePost?.(postId, body)
    const trimmed = body.trim()
    patchApp(id, (item) => ({
      ...item,
      postBody: trimmed,
      description: item.postType === 'text' || item.postType === 'short' ? trimmed : item.description,
      updatedAt: new Date().toISOString(),
    }))
  }, [patchApp, snapshot.apps])

  const createTextPost = useCallback(async (body: string, mediaUrl?: string | null) => {
    if (!appyAdapter.createTextPost) throw new Error('Text posts require Supabase.')
    const created = await appyAdapter.createTextPost(body, mediaUrl)
    update((current) => ({ ...current, apps: [created, ...current.apps] }))
    return created.id
  }, [update])

  const createShortPost = useCallback(async (body: string, videoUrl: string, durationSeconds?: number | null) => {
    if (!appyAdapter.createShortPost) throw new Error('Shorts require Supabase.')
    const created = await appyAdapter.createShortPost(body, videoUrl, durationSeconds)
    update((current) => ({ ...current, apps: [created, ...current.apps] }))
    return created.id
  }, [update])

  const staffBanUser = useCallback(async (userId: string, reason?: string) => {
    if (!appyAdapter.staffBanUser) throw new Error('Staff tools require Supabase.')
    await appyAdapter.staffBanUser(userId, reason)
    update((current) => ({
      ...current,
      users: current.users.map((user) => user.id === userId ? { ...user, banned: true, banReason: reason ?? null } : user),
    }))
  }, [update])

  const staffUnbanUser = useCallback(async (userId: string) => {
    if (!appyAdapter.staffUnbanUser) throw new Error('Staff tools require Supabase.')
    await appyAdapter.staffUnbanUser(userId)
    update((current) => ({
      ...current,
      users: current.users.map((user) => user.id === userId ? { ...user, banned: false, banReason: null } : user),
    }))
  }, [update])

  const staffSetModerator = useCallback(async (userId: string, enabled: boolean) => {
    if (!appyAdapter.staffSetModerator) throw new Error('Staff tools require Supabase.')
    await appyAdapter.staffSetModerator(userId, enabled)
    update((current) => ({
      ...current,
      users: current.users.map((user) => user.id === userId
        ? {
          ...user,
          staffRole: enabled ? 'moderator' : null,
          verified: enabled ? true : user.verified,
          verificationStatus: enabled ? 'verified' : user.verificationStatus,
        }
        : user),
    }))
  }, [update])

  const requestSubscriberVerification = useCallback(async (userId?: string) => {
    if (!appyAdapter.requestSubscriberVerification) throw new Error('Verification requires Supabase.')
    const result = await appyAdapter.requestSubscriberVerification(userId)
    if (result.status === 'verified' || result.status === 'already_verified') {
      const target = userId ?? snapshot.currentUserId
      update((current) => ({
        ...current,
        users: current.users.map((user) => user.id === target
          ? { ...user, verified: true, verificationStatus: 'verified' }
          : user),
      }))
    }
    return result
  }, [snapshot.currentUserId, update])

  const moderatePostImage = useCallback(async (file: File, options?: { maxBytes?: number }) => {
    if (!appyAdapter.moderatePostImage) throw new Error('Image safety review requires Supabase.')
    await appyAdapter.moderatePostImage(file, options)
  }, [])

  const moderatePostVideo = useCallback(async (file: File, options?: { maxBytes?: number; maxSeconds?: number }) => {
    if (!appyAdapter.moderatePostVideo) throw new Error('Video safety review requires Supabase.')
    await appyAdapter.moderatePostVideo(file, options)
  }, [])

  const reportContent = useCallback(async (input: { reason: string; details?: string; postId?: string; projectId?: string; reportedUserId?: string }) => {
    if (!appyAdapter.reportContent) throw new Error('Reporting requires Supabase.')
    await appyAdapter.reportContent(input)
  }, [])

  const staffAdminStats = useCallback(async () => {
    if (!appyAdapter.staffAdminStats) throw new Error('Admin tools require Supabase.')
    return appyAdapter.staffAdminStats()
  }, [])
  const staffListBannedUsers = useCallback(async () => {
    if (!appyAdapter.staffListBannedUsers) throw new Error('Admin tools require Supabase.')
    return appyAdapter.staffListBannedUsers()
  }, [])
  const staffListReports = useCallback(async (status?: string | null) => {
    if (!appyAdapter.staffListReports) throw new Error('Admin tools require Supabase.')
    return appyAdapter.staffListReports(status)
  }, [])
  const staffUpdateReport = useCallback(async (reportId: string, status: import('./model').AdminReportStatus, note?: string) => {
    if (!appyAdapter.staffUpdateReport) throw new Error('Admin tools require Supabase.')
    await appyAdapter.staffUpdateReport(reportId, status, note)
  }, [])
  const staffListRecentPosts = useCallback(async (limit?: number) => {
    if (!appyAdapter.staffListRecentPosts) throw new Error('Admin tools require Supabase.')
    return appyAdapter.staffListRecentPosts(limit)
  }, [])
  const staffMassDeletePosts = useCallback(async (postIds: string[]) => {
    if (!appyAdapter.staffMassDeletePosts) throw new Error('Admin tools require Supabase.')
    const count = await appyAdapter.staffMassDeletePosts(postIds)
    if (count > 0) {
      const removed = new Set(postIds)
      update((current) => ({
        ...current,
        apps: current.apps.filter((app) => !app.postId || !removed.has(app.postId)),
      }))
    }
    return count
  }, [update])

  const setProjectCover = useCallback(async (id: string, coverUrl: string | null) => {
    await appyAdapter.setProjectCover?.(id, coverUrl)
    patchApp(id, (item) => ({ ...item, coverUrl }))
  }, [patchApp])

  const uploadMedia = useCallback(async (
    kind: 'avatar' | 'banner' | 'cover' | 'post' | 'short',
    file: File,
    projectId?: string,
    options?: { maxBytes?: number },
  ) => {
    if (!appyAdapter.uploadMedia) throw new Error('Media uploads require Supabase.')
    return appyAdapter.uploadMedia(kind, file, projectId, options)
  }, [])

  const restoreVersion = useCallback(async (id: string, version: number) => {
    const app = snapshot.apps.find((item) => item.id === id)
    const target = app?.versions.find((item) => item.number === version)
    if (!app || !target) throw new Error('That version is no longer available.')
    const restored = appyAdapter.restoreVersion
      ? await appyAdapter.restoreVersion(app, target)
      : await Promise.resolve((() => {
        const number = Math.max(0, ...app.versions.map((item) => item.number)) + 1
        const current: AppVersion = { ...target, number, note: `Restored from version ${target.number}`, createdAt: new Date().toISOString() }
        return { current, versions: [...app.versions, current], status: app.status === 'published' ? 'published' as const : 'ready' as const }
      })())
    patchApp(id, (currentApp) => ({
      ...currentApp,
      title: restored.current.title ?? currentApp.title,
      description: restored.current.description ?? currentApp.description,
      srcDoc: restored.current.srcDoc,
      version: restored.current.number,
      versions: restored.versions,
      status: restored.status,
      updatedAt: restored.current.createdAt,
    }))
    return restored.current.number
  }, [patchApp, snapshot.apps])

  const markNotificationsRead = useCallback(() => { update((current) => ({ ...current, notifications: current.notifications.map((item) => ({ ...item, read: true })) })); void appyAdapter.markNotificationsRead?.() }, [update])
  const markNotificationRead = useCallback((id: string) => { update((current) => ({ ...current, notifications: current.notifications.map((item) => item.id === id ? { ...item, read: true } : item) })); void appyAdapter.markNotificationsRead?.([id]) }, [update])

  const setSetting = useCallback(<K extends keyof AppySnapshot['settings']>(key: K, value: AppySnapshot['settings'][K]) => {
    update((current) => ({ ...current, settings: { ...current.settings, [key]: value } }))
  }, [update])

  const setIntelligence = useCallback((value: Intelligence) => setSetting('intelligence', value), [setSetting])
  const configureDailyGame = useCallback(async (schedule: DailySchedule) => {
    const normalized = { ...schedule, hour: Math.min(23, Math.max(0, Math.round(schedule.hour))) }
    await appyAdapter.setDailySchedule?.(normalized)
    update((current) => ({
      ...current,
      settings: {
        ...current.settings,
        dailyGame: normalized.enabled,
        dailyGameHour: normalized.hour,
        dailyGameIntelligence: normalized.intelligence,
      },
    }))
  }, [update])

  const updateProfile = useCallback(async (profile: ProfileUpdate) => {
    const normalized = {
      name: profile.name.trim().slice(0, 50),
      handle: profile.handle.trim().replace(/^@/, '').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 30),
      bio: profile.bio.trim().slice(0, 160),
      avatarUrl: profile.avatarUrl,
      bannerUrl: profile.bannerUrl,
    }
    if (!normalized.name || normalized.handle.length < 2) throw new Error('Add a name and a handle with at least 2 characters.')
    await appyAdapter.updateProfile?.(normalized)
    update((current) => ({
      ...current,
      users: current.users.map((user) => user.id === current.currentUserId ? {
        ...user,
        name: normalized.name,
        handle: normalized.handle,
        bio: normalized.bio,
        avatarUrl: normalized.avatarUrl !== undefined ? normalized.avatarUrl : user.avatarUrl,
        bannerUrl: normalized.bannerUrl !== undefined ? normalized.bannerUrl : user.bannerUrl,
        avatar: normalized.name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || user.avatar,
      } : user),
    }))
  }, [update])

  const setProfileVisibility = useCallback(async (visibility: AppySnapshot['settings']['profileVisibility']) => {
    await appyAdapter.setProfileVisibility?.(visibility)
    setSetting('profileVisibility', visibility)
  }, [setSetting])
  const setPlan = useCallback((plan: AppySnapshot['billing']['plan']) => {
    const allowance = plan === 'studio' ? 24 : plan === 'creator' ? 18 : 0
    update((current) => ({ ...current, billing: { ...current.billing, plan, creditAllowance: allowance, creditBalance: Math.max(current.billing.creditBalance, allowance) } }))
  }, [update])
  const addCredits = useCallback((amount: number) => update((current) => ({ ...current, billing: { ...current.billing, creditBalance: current.billing.creditBalance + amount } })), [update])
  const purchasePlan = useCallback(async (plan: 'creator' | 'pro') => {
    if (!appyAdapter.purchasePlan) throw new Error('Secure Stripe checkout is only available when Appy is connected to Supabase.')
    const checkout = await appyAdapter.purchasePlan(plan)
    if (!checkout) throw new Error('Stripe did not return a checkout URL. Check that your account email is verified and billing secrets are configured.')
    window.location.assign(checkout)
  }, [])
  const purchaseCredits = useCallback(async (amount: number) => {
    if (!appyAdapter.purchaseCredits) throw new Error('Secure Stripe checkout is only available when Appy is connected to Supabase.')
    const checkout = await appyAdapter.purchaseCredits(amount)
    if (!checkout) throw new Error('Stripe did not return a checkout URL. Check that your account email is verified and billing secrets are configured.')
    window.location.assign(checkout)
  }, [])
  const openBillingPortal = useCallback(async () => {
    if (!appyAdapter.openBillingPortal) throw new Error('Billing management is only available when Appy is connected to Supabase.')
    const portal = await appyAdapter.openBillingPortal()
    if (!portal) throw new Error('Stripe did not return a billing portal URL.')
    window.location.assign(portal)
    return true
  }, [])
  const secureAccount = useCallback(async (email: string, password: string) => {
    if (!appyAdapter.secureAccount) throw new Error('Account security is not available in this build.')
    const result = await appyAdapter.secureAccount(email.trim(), password)
    update((current) => ({ ...current, account: { status: result.verificationRequired ? 'verification-pending' : 'secured', email: result.email } }))
    return result
  }, [update])
  const signUp = useCallback(async (email: string, password: string) => {
    if (!appyAdapter.signUp) throw new Error('Account creation is not available in this build.')
    const result = await appyAdapter.signUp(email.trim(), password)
    const next = await appyAdapter.loadSnapshot()
    if (next) setSnapshot(next)
    else update((current) => ({ ...current, account: { status: result.verificationRequired ? 'verification-pending' : 'secured', email: result.email } }))
    return result
  }, [update])
  const signIn = useCallback(async (email: string, password: string) => {
    if (!appyAdapter.signIn) throw new Error('Sign-in is not available in this build.')
    const result = await appyAdapter.signIn(email.trim(), password)
    const next = await appyAdapter.loadSnapshot()
    if (!next) throw new Error('Appy signed in but could not load the account.')
    setSnapshot(next)
    return result
  }, [])
  const mfaLoginRequired = useCallback(async () => appyAdapter.mfaLoginRequired?.() ?? false, [])
  const verifyMfaLogin = useCallback(async (code: string) => {
    if (!appyAdapter.verifyMfaLogin) throw new Error('Two-step authentication requires Supabase.')
    await appyAdapter.verifyMfaLogin(code)
  }, [])
  const signOut = useCallback(async () => {
    if (!appyAdapter.signOut) throw new Error('Sign-out is not available in this build.')
    await appyAdapter.signOut()
    const next = await appyAdapter.loadSnapshot()
    if (!next) throw new Error('Appy signed out but could not start a fresh session.')
    setSnapshot(next)
  }, [])
  const resetPassword = useCallback(async (email: string) => {
    if (!appyAdapter.resetPassword) throw new Error('Password reset requires Supabase.')
    await appyAdapter.resetPassword(email)
  }, [])
  const updatePassword = useCallback(async (password: string) => {
    if (!appyAdapter.updatePassword) throw new Error('Password updates require Supabase.')
    await appyAdapter.updatePassword(password)
  }, [])
  const listMfaFactors = useCallback(async () => appyAdapter.listMfaFactors?.() ?? [], [])
  const enrollMfa = useCallback(async () => {
    if (!appyAdapter.enrollMfa) throw new Error('Two-step authentication requires Supabase.')
    return appyAdapter.enrollMfa()
  }, [])
  const verifyMfaEnrollment = useCallback(async (factorId: string, code: string) => {
    if (!appyAdapter.verifyMfaEnrollment) throw new Error('Two-step authentication requires Supabase.')
    await appyAdapter.verifyMfaEnrollment(factorId, code)
    update((current) => ({ ...current, account: { ...current.account, mfaEnabled: true } }))
  }, [update])
  const unenrollMfa = useCallback(async (factorId: string) => {
    if (!appyAdapter.unenrollMfa) throw new Error('Two-step authentication requires Supabase.')
    await appyAdapter.unenrollMfa(factorId)
    update((current) => ({ ...current, account: { ...current.account, mfaEnabled: false } }))
  }, [update])
  const completeOnboarding = useCallback(async (name: string, handle: string) => {
    const me = snapshot.users.find((user) => user.id === snapshot.currentUserId)
    const normalized = {
      name: name.trim().slice(0, 50),
      handle: handle.trim().replace(/^@/, '').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 30),
      bio: me?.bio ?? '',
    }
    if (!normalized.name || normalized.handle.length < 2) throw new Error('Add a name and a handle with at least 2 characters.')
    await appyAdapter.updateProfile?.(normalized)
    update((current) => ({
      ...current,
      onboarded: true,
      users: current.users.map((user) => user.id === current.currentUserId ? {
        ...user,
        ...normalized,
        avatar: normalized.name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || user.avatar,
      } : user),
    }))
  }, [snapshot.currentUserId, snapshot.users, update])
  const resetDemo = useCallback(() => { setSnapshot(clone()); setLoadError(null); setLoading(false) }, [])

  const runtimeGet = useCallback((projectId: string, key: string) => appyAdapter.runtimeGet?.(projectId, key) ?? Promise.resolve(null), [])
  const runtimeSet = useCallback((projectId: string, key: string, value: unknown) => appyAdapter.runtimeSet?.(projectId, key, value) ?? Promise.resolve(), [])
  const runtimeDelete = useCallback((projectId: string, key: string) => appyAdapter.runtimeDelete?.(projectId, key) ?? Promise.resolve(), [])
  const runtimePublicProfile = useCallback((projectId: string) => appyAdapter.runtimePublicProfile?.(projectId) ?? Promise.resolve(null), [])

  const currentUser = useMemo(() => snapshot.users.find((user) => user.id === snapshot.currentUserId) ?? snapshot.users[0], [snapshot])
  return { snapshot, currentUser, loading, refreshing, loadError, retryLoad, softRefresh, actionError, clearActionError, mode: appyAdapter.mode ?? 'demo', toggleLike, toggleSave, toggleRepost, recordPlay, recordImpression, toggleFollow, addComment, toggleCommentLike, toggleCommentRepost, build, publish, revise, cancelBuild, restoreVersion, deletePost, updatePost, createTextPost, createShortPost, setProjectCover, uploadMedia, staffBanUser, staffUnbanUser, staffSetModerator, requestSubscriberVerification, moderatePostImage, moderatePostVideo, reportContent, staffAdminStats, staffListBannedUsers, staffListReports, staffUpdateReport, staffListRecentPosts, staffMassDeletePosts, markNotificationsRead, markNotificationRead, setSetting, setIntelligence, configureDailyGame, updateProfile, setProfileVisibility, setPlan, addCredits, purchasePlan, purchaseCredits, openBillingPortal, secureAccount, signUp, signIn, signOut, resetPassword, updatePassword, listMfaFactors, enrollMfa, verifyMfaEnrollment, unenrollMfa, mfaLoginRequired, verifyMfaLogin, completeOnboarding, resetDemo, runtimeGet, runtimeSet, runtimeDelete, runtimePublicProfile }
}
